import { Chip, Grid, makeStyles, Tooltip } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import NotificationsActiveActive from '@material-ui/icons/NotificationsActiveOutlined';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOffOutlined';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from 'src/constants';
import useApi from 'src/hooks/useApi';
import { SearchAgentDTOStrict } from 'src/hooks/useSearchAgents';
import { useSearchAgentUpdate } from 'src/hooks/useSearchAgentUpdate';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { useUser } from 'src/hooks/useUser';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import ApiService from 'src/modules/api-service';
import { getSystemAgentName } from 'src/modules/system-agents';

import { transformForSubmit } from './filter/lib';
import { useFilter } from './filter/useFilter';

type WatchlistFilterchipProps = {
  filterStateId: string | undefined;
  searchAgents: SearchAgentDTOStrict[];
};

const useStyles = makeStyles(() => ({
  'chip-active': {
    backgroundColor: green[500],
    color: 'white',
    '&:hover, &:focus': {
      backgroundColor: green[500],
    },
    '& svg': {
      color: 'white',
    },
  },
  chip: {
    color: green[600],
    borderColor: green[600],
    '& svg': {
      color: green[600],
    },
  },
}));

const WatchlistFilterchip = ({ filterStateId, searchAgents }: WatchlistFilterchipProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { defaultFilter, setFilter } = useFilter();
  const { data: user } = useUser();
  const { mutate: updateSearchAgent } = useSearchAgentUpdate();
  const { fetch: fetchCreate } = useApi();
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { valuationCountry: globalValuationCountry } = useValuationSettings();

  const watchlistSearchAgentName = getSystemAgentName(
    'watchlist',
    user?.user?.authenticationProvider ?? '',
    user?.user?.id ?? '',
  );
  const exists = searchAgents.find((agent) => agent.searchAgentDisplayName === watchlistSearchAgentName);

  function handleActivateWatchlistFilter(): void {
    setFilter({ ...defaultFilter, watchlist: true, id: watchlistSearchAgentName });
  }

  async function handleToggleNotification() {
    if (exists) {
      updateSearchAgent({ ...exists, active: !exists.active });
    } else {
      TagManager.dataLayer({ dataLayer: { event: 'searchagent-save' } });
      const newSearchAgent = transformForSubmit({ ...defaultFilter, watchlist: true });
      try {
        await fetchCreate(
          ApiService.searchAgent.searchAgentControllerCreateSearchAgent({
            ...newSearchAgent,
            version: '2',
            active: true,
            favorite: false,
            valuationCountry: globalValuationCountry ?? undefined,
            searchAgentDisplayName: watchlistSearchAgentName,
          }),
        );
        queryClient.invalidateQueries([QueryKeys.searchAgents]);
      } catch (error) {
        console.error('[WatchlistFilterchip] failed to create watchlist search agent', error);
        snackbar.showError(t('searchAgentsPage.messages.error'));
      }
    }
  }

  return (
    <Grid item key={watchlistSearchAgentName}>
      <Tooltip title={t('watchlist.showOwnWatchlist')} placement="top">
        <Chip
          size="small"
          variant={filterStateId === watchlistSearchAgentName ? 'default' : 'outlined'}
          label={t('watchlist.watchlist')}
          className={clsx(filterStateId === watchlistSearchAgentName ? classes['chip-active'] : classes.chip)}
          onClick={() => handleActivateWatchlistFilter()}
          onDelete={() => handleToggleNotification()}
          deleteIcon={exists && exists.active ? <NotificationsActiveActive /> : <NotificationsOffIcon />}
        />
      </Tooltip>
    </Grid>
  );
};

export default WatchlistFilterchip;
